import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { ViewInCRMButton } from "components/integrations/ProviderButtons";
import { NativeProviderType } from "components/integrations/providerMetadata";
import { friendlyProviderName } from "components/integrations/util";
import toast from "components/ui/toast/Toast";
import { ResponseError, SendOpportunityResponse } from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";
import { refusedFieldsFrom } from "lib/integrations/crmSendErrors";
import { TrackingProvider } from "lib/tracking";

type RequestParams = {
  guid: string;
  data: Record<string, unknown>;
};

export function useSendOpportunity(
  providerType: NativeProviderType,
  guid: string,
  name: string,
  options?: UseMutationOptions<SendOpportunityResponse, unknown, RequestParams, unknown>,
) {
  const api = useIntegrationApi();
  const queryClient = useQueryClient();

  const providerName = friendlyProviderName[providerType];
  const shortenedRecordName = name.slice(0, 30);
  const messageKey = "crmOpportunity";

  return useMutation(
    ({ guid, data }: RequestParams) =>
      api.sendOpportunity({
        sendOpportunityRequest: { guid: guid, fields: JSON.stringify(data) },
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
        // The externalURL is currently an optional return because we don't want to make it required on the datamodel
        // This is to hedge for the future where we may integrate with messaging services
        toast.success(
          data.externalUrl
            ? `${shortenedRecordName}... sent to ${providerName}`
            : `Successfully sent to ${providerName}`,
          {
            action: data.externalUrl ? (
              <TrackingProvider
                data={{
                  "Context source": "Pop-up message top screen",
                  "Connected provider": providerType,
                }}
              >
                <ViewInCRMButton providerType={providerType} externalUrl={data.externalUrl} />
              </TrackingProvider>
            ) : undefined,
            duration: 5000,
          },
        );
      },
      onError: async (err: unknown) => {
        // This modal closes as it submits, so the toast is the only place a refusal can be read.
        // Naming the properties matters the most here: without them the customer is told only to
        // write in, when the field they would have to change is one they can see in their own CRM.
        const refused = await refusedFieldsFrom(err);
        if (refused.length > 0) {
          toast.error(
            `${providerName} would not accept ${refused.map((field) => field.name).join(", ")}`,
            { duration: 8000 },
          );
        } else if (err instanceof ResponseError && err.response.status === 400) {
          toast.error(
            "Please contact team@stotles.com to modify your CRM settings. The form is out of sync with your CRM.",
            {
              duration: 8000,
            },
          );
        } else {
          toast.error(`Something went wrong sending the opportunity to the CRM`);
        }
      },
      // TODO: At some point swap this to using toast.promise so it smoothly transitions to the new toast
      // rather than deleting the current one and showing a new one
      onSettled: () => {
        toast.dismiss(messageKey);
        void queryClient.invalidateQueries({ queryKey: ["opportunityCrmDetails", guid] });
      },
      onMutate: () => {
        toast.loading(
          <>
            Sending <b>{shortenedRecordName}...</b> to {providerName}
          </>,
          {
            id: messageKey,
          },
        );
      },
    },
  );
}
